import { ReactNode, useState, useEffect } from "react";
import { EllipsisOutlined } from "@ant-design/icons";
import {
  Typography,
  Button,
  Dropdown,
  Menu,
  Modal,
  Input,
  Pagination,
} from "antd";
import { useSearchParams, useLocation } from "react-router-dom";
import { ProjectRelatedTabs } from "@/features/project-related-tabs";
import {
  SupervisionCardsList,
  DocumentPackage,
  generateMockPackages,
  TabVariants,
  FilterVariants,
  AddToPackageModal,
  DocumentPackageOption,
} from "@/features/supervision-cards-list";
import { MindMappingIcon, WriteIcon } from "@/shared/assets/svg/icons";
import { useFlags } from "@/shared/lib/flags";
import { useCoordinator } from "@/shared/lib/hooks";
import { routes, useNavigate } from "@/shared/lib/react-router";
import { Breadcrumbs } from "@/shared/ui";
import { useDebounce, isUnifiedRoute } from "@shared/utils";
import { openLinkMessagesModal } from "../link-messages-modal-opener/open-link-messages-modal";
import { NotificationViewModal } from "../notification-view-modal";
import { SupervisionNotificationsAction } from "../supervision-notifications-action";
import { SupervisionNotificationsFilter } from "../supervision-notifications-filter";
import "./supervision-notifications-page.scss";

const { Title } = Typography;

const generateBreadcrumbs = (projectId: string, supervisionId: string) => {
  return [
    { path: routes.supervision.root(projectId), label: "Надзороне дело" },
    {
      path: routes.supervision.notifications(projectId, supervisionId),
      label: "«Бизнес-центр Невский»",
    },
  ];
};

const tabVariantsArray: TabVariants[] = ["incoming", "outgoing", "processing"];

export const SupervisionNotificationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isUnified = isUnifiedRoute(location.pathname);
  
  const currentTab = (searchParams.get("tab") as TabVariants) || "incoming";
  const currentFilter = (searchParams.get("filter") as FilterVariants) || "all";
  const isModalOpen = searchParams.get("createPackage") === "true";

  const [packages, setPackages] = useState<DocumentPackage[]>([]);
  const [packageName, setPackageName] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [selectedCardForPackaging, setSelectedCardForPackaging] =
    useState<DocumentPackage | null>(null);
  const [packageModalOpen, setPackageModalOpen] = useState(false);

  const navigate = useNavigate();
  const { projectId, supervisionId } = useCoordinator();
  const isIntegrationSettingsEnabled = useFlags(
    "supervisionIntegrationSettingsEnabled",
  );

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const menu = (
    <Menu>
      <Menu.Item
        key="1"
        icon={<WriteIcon />}
        onClick={() =>
          handleNavigate(
            routes.supervision.adminSettings(projectId, supervisionId),
          )
        }
      >
        Настройки администрирования
      </Menu.Item>
      {isIntegrationSettingsEnabled && (
        <Menu.Item
          key="2"
          icon={<MindMappingIcon />}
          onClick={() =>
            handleNavigate(
              routes.supervision.integrationSettings(projectId, supervisionId),
            )
          }
        >
          Настройки интеграции
        </Menu.Item>
      )}
    </Menu>
  );

  // Динамический список фильтров - начинаем с "Все", добавляем новые при создании пакето
  const [filterVariantsArray, setFilterVariantsArray] = useState<
    FilterVariants[]
  >(["all"]);

  const debouncedSearchValue = useDebounce(searchValue, 300);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchValue]);

  useEffect(() => {
    const initialPackages = generateMockPackages(30);
    setPackages(initialPackages);
  }, []);

  const filteredPackages = packages.filter((pkg) => {
    const tabMatch = pkg.tab === currentTab;

    const packageMatch =
      currentFilter === "all" ||
      (pkg.packages && pkg.packages.includes(currentFilter));

    const searchMatch =
      !debouncedSearchValue ||
      pkg.title.toLowerCase().includes(debouncedSearchValue.toLowerCase());

    return tabMatch && packageMatch && searchMatch;
  });

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPackages = filteredPackages.slice(startIndex, endIndex);

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    }
  };

  const updateTab = (newTab: TabVariants) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", newTab);
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const updateFilter = (newFilter: FilterVariants) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("filter", newFilter);
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const openModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("createPackage", "true");
    setSearchParams(newParams);
  };

  const closeModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("createPackage");
    setSearchParams(newParams);
    setPackageName("");
  };

  const createPackage = () => {
    if (packageName.trim()) {
      const packageNameTrimmed = packageName.trim();

      const newFilterId = packageNameTrimmed
        .toLowerCase()
        .replace(/\s+/g, "_") as FilterVariants;

      if (!filterVariantsArray.includes(newFilterId)) {
        setFilterVariantsArray((prev) => [...prev, newFilterId]);
      }

      closeModal();
    }
  };

  const availablePackages: DocumentPackageOption[] = [
    { id: "all", name: "Все", selected: true },
    ...filterVariantsArray.slice(1).map((filter) => {
      let name = "";
      if (filter === "archive") {
        name = "Архив";
      } else if (filter === "important") {
        name = "Важные";
      } else {
        name = (filter as string)
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l: string) => l.toUpperCase());
      }

      return {
        id: filter,
        name,
        selected: selectedCardForPackaging?.packages?.includes(filter) || false,
      };
    }),
  ];

  const handleAddToPackage = (cardId: string) => {
    const card = packages.find((pkg) => pkg.id === cardId);
    if (card) {
      setSelectedCardForPackaging(card);
      setPackageModalOpen(true);
    }
  };

  const handlePackageModalConfirm = (selectedPackages: string[]) => {
    if (selectedCardForPackaging) {
      setPackages((prev) =>
        prev.map((pkg) =>
          pkg.id === selectedCardForPackaging.id
            ? { ...pkg, packages: selectedPackages }
            : pkg,
        ),
      );
    }
    setSelectedCardForPackaging(null);
  };


  const tabCounts = {
    incoming: packages.filter((pkg) => pkg.tab === "incoming").length,
    outgoing: packages.filter((pkg) => pkg.tab === "outgoing").length,
    processing: packages.filter((pkg) => pkg.tab === "processing").length,
  };
  return (
    <div>
      <Breadcrumbs routes={generateBreadcrumbs(projectId, supervisionId)} disableFirst={isUnified ? 3 : 0} />

      <div className="supervision-notifications-page__header">
        <Title level={3} style={{ lineHeight: "32px" }}>
          Бизнес-центр «Невский»
        </Title>
        <Dropdown overlay={menu} trigger={["hover"]}>
          <Button
            shape="circle"
            icon={(<EllipsisOutlined />) as ReactNode}
            style={{
              borderRadius: "10px",
            }}
          />
        </Dropdown>
      </div>

      <ProjectRelatedTabs
        tabs={tabVariantsArray}
        tabsTranslate={[
          `Входящие ${tabCounts.incoming}`,
          `Исходящие ${tabCounts.outgoing}`,
          `На обработку ${tabCounts.processing}`,
        ]}
        activeTab={currentTab}
        setActiveTab={(tab: string) => updateTab(tab as TabVariants)}
      />

      <SupervisionNotificationsAction
        filterVariantsArray={filterVariantsArray}
        currentFilter={currentFilter}
        updateFilter={updateFilter}
        openModal={openModal}
      />

      <SupervisionNotificationsFilter
        isFullWidth
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
      />

      <Modal
        title="Создание пакета документов"
        open={isModalOpen}
        onOk={createPackage}
        onCancel={closeModal}
        okText="Создать"
        cancelText="Отмена"
        okButtonProps={{ disabled: !packageName.trim() }}
      >
        <Input
          placeholder="Название нового пакета"
          value={packageName}
          onChange={(e) => setPackageName(e.target.value)}
          onPressEnter={createPackage}
        />
      </Modal>

      <NotificationViewModal packages={packages} />

      <SupervisionCardsList 
        packages={currentPackages}
        onAddToPackage={handleAddToPackage}
        onLinkMessage={(id) => openLinkMessagesModal(id)}
      />

      <Pagination
        current={currentPage}
        total={filteredPackages.length}
        pageSize={pageSize}
        showSizeChanger
        showTotal={(total, range) => `${range[0]}-${range[1]} из ${total}`}
        pageSizeOptions={["10", "20", "50", "100"]}
        style={{ textAlign: "center", marginTop: "24px" }}
        onChange={handlePageChange}
        onShowSizeChange={handlePageChange}
        simple
      />

      <AddToPackageModal
        open={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        onConfirm={handlePackageModalConfirm}
        availablePackages={availablePackages}
        cardTitle={selectedCardForPackaging?.title}
      />
    </div>
  );
};
